// libraries
import React, { useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components/native';
import { useForm } from 'react-hook-form';
import { StatusBar, ListRenderItemInfo, FlatList } from 'react-native';
import { RouteProp, useRoute } from '@react-navigation/native';

// components
import {
  FilterCheckbox,
  CustomModal,
  CustomModalProps,
  FullScreenLoader,
  SelectInput,
} from '@components/molecules';
import {
  CustomIcon,
  CustomText,
  DivColumn,
  Divider,
  SmallButton,
  SpacerColumn,
} from '@components/atoms';

// styles
import { genericStyles } from '@styles/genericStyles';

// misc
import { DashboardStackParamList } from '@utils/navigation';
import { useGetDeliveryCourseAndRiders } from '@hooks/useGetDeliveryCourseAndRiders';

export interface SortingModalProps extends Omit<CustomModalProps, 'children'> {
  onSortingChange: (settings: SortingType) => void;
}

export type SortingType = {
  category_id: SORTING_TYPE;
  group_id: number | null;
};

export type SortingCategory = {
  id: 'follower' | 'following' | number;
  name: string;
};

export type SORTING_TYPE = 'YEAR_GROUP' | 'A-TO-Z' | 'HIDE_COURSE_COMPLETED_RIDERS';
export type SORTING_DATA_TYPE = { id: SORTING_TYPE; title: string };

const SORTING_DATA: SORTING_DATA_TYPE[] = [
  {
    id: 'YEAR_GROUP',
    title: 'Year Group Order',
  },
  {
    id: 'A-TO-Z',
    title: 'Alphabetical Order',
  },
];

export const SortingModal = ({ onSortingChange, ...restProps }: SortingModalProps) => {
  // variables
  const { colors } = useTheme();
  const { control, setValue, handleSubmit } = useForm<SortingType>({
    mode: 'all',
  });
  const { delivery } = useGetDeliveryCourseAndRiders();
  const [currentFilter, setCurrentFilter] = useState<SortingType['category_id']>(null);
  const [currentGroupFilter, setCurrentGroupFilter] = useState<SortingType['group_id']>(null);

  const {
    params: { deliveryId, courseId, isClearRiders },
  } = useRoute<RouteProp<DashboardStackParamList, 'allRiders'>>();

  if (
    courseId &&
    SORTING_DATA.findIndex(dataObj => dataObj.id === 'HIDE_COURSE_COMPLETED_RIDERS') === -1
  ) {
    SORTING_DATA.push({
      id: 'HIDE_COURSE_COMPLETED_RIDERS',
      title: 'Hide Course Completed Riders',
    });
  } else if (
    !courseId &&
    SORTING_DATA.findIndex(dataObj => dataObj.id === 'HIDE_COURSE_COMPLETED_RIDERS') !== -1
  ) {
    SORTING_DATA.pop();
  }

  // hooks
  useEffect(() => {
    setValue('category_id', currentFilter);
    setValue('group_id', Number(currentGroupFilter));
  }, [restProps.isVisible]);

  // functions
  const onApply = (data: SortingType) => {
    onSortingChange(data);
    setCurrentFilter(data['category_id']);
    setCurrentGroupFilter(Number(data['group_id']));
    restProps.onClose();
  };

  const onReset = async () => {
    onSortingChange({ category_id: null, group_id: null });
    setCurrentFilter(null);
    setCurrentGroupFilter(null);
    setValue('category_id', null);
    setValue('group_id', null);
    restProps.onClose();
  };

  const groupData = useMemo(() => {
    return delivery.groups.map(group => ({
      label: group.name.toString(),
      value: group.id.toString(),
    }));
  }, [delivery.groups]);

  const renderCategory = ({ item }: ListRenderItemInfo<SORTING_DATA_TYPE>) => (
    <DivColumn key={item.id}>
      <FilterCheckbox
        control={control}
        name={'category_id'}
        text={item.title}
        defaultValue={item.id}
      />

      <SpacerColumn size={0.25} />
    </DivColumn>
  );

  // returns
  return (
    <CustomModal
      {...restProps}
      renderHeader={() => null}
      animationIn="slideInUp"
      animationOut="slideOutDown">
      <HeaderContainer style={genericStyles.rowWithCenterAndSB}>
        <CustomIcon name="x" onPress={restProps.onClose} />
        <CustomText color="text" font="bodyBold" size={16}>
          Sort / Filter
        </CustomText>
        <CustomText color="text" font="bodyRegular" onPress={onReset}>
          Reset
        </CustomText>
      </HeaderContainer>
      <Divider color="silver" />

      {restProps.isVisible && <StatusBar backgroundColor={colors.white} barStyle="dark-content" />}

      <FullScreenLoader isLoading={false}>
        <>
          <Container>
            <SpacerColumn size={0.5} />
            <Heading color="text" font="bodyMedium" size={16} />

            <FlatList
              data={SORTING_DATA}
              renderItem={renderCategory}
              keyExtractor={item => item.id.toString()}
            />
            {groupData.length && (
              <DivColumn style={{ width: '100%', height: 80 }}>
                <CustomText color="text" font="bodyRegular">
                  Filter by Group
                </CustomText>
                <SelectInput
                  name="group_id"
                  control={control}
                  data={groupData}
                  onChangeValue={setValue}
                  title=""
                  placeHolder="Select group to filter by"
                  sheetTitle="Filter by Group"
                  rules={{ required: false }}
                />
              </DivColumn>
            )}
          </Container>

          <Footer>
            <SmallButton title="Apply" onPress={handleSubmit(onApply)} />
          </Footer>
        </>
      </FullScreenLoader>
    </CustomModal>
  );
};

const Container = styled.View(({ theme: { layout, colors } }) => ({
  flex: 1,
  backgroundColor: colors.primaryBackground,
  paddingHorizontal: layout.padding_x2,
}));

const HeaderContainer = styled.View(({ theme: { layout, colors } }) => ({
  backgroundColor: colors.primaryBackground,
  paddingBottom: layout.padding_x2,
  paddingHorizontal: layout.padding_x2,
}));

const Heading = styled(CustomText)(({ theme: { layout } }) => ({
  ...genericStyles.upperCase,
  paddingBottom: layout.padding * 0.2,
}));

const Footer = styled.View(({ theme: { layout, colors } }) => ({
  // todo-ios: implement ios shadow
  elevation: 0.5,
  paddingHorizontal: layout.padding_x2,
  paddingVertical: layout.padding_x2,
  backgroundColor: colors.primaryBackground,
}));
